// ABOUTME: Transformation schema mapping TechLog trip updates to Rusada maintenance TechLogs.
import {
  formatRusadaTechLogNo,
  formatRusadaTechLogTimestamp,
} from '../tech-log-no';
import { TechLogToRusadaTripUpdateInput } from './validation';

/**
 * Trip updates use Maintenance TechLog Category to create "dummy" TechLogs
 * that won't conflict with existing sectors. This allows totals adjustments
 * without timestamp overlap validation.
 *
 * Per Veryon support: "Maintenance category tech log is dummy tech log through
 * which we can update aircraft totals by updating with required usage."
 *
 * For Affinity specifically, we also set the "Not Flown" journey type (id: 15)
 * if available, otherwise fallback to EGR (id: 6).
 *
 * Oil/Fuel units: Affinity uses KGs for Fuel, Quarts for Oil. These are
 * configured in TechLog by operators. Currently not setting units in Veryon
 * (field is optional) until proper configuration is in place.
 */

export const techlogTripUpdateToRusadaSchema = {
  // Composite Aircraft Log No keeps the trip number recognisable against the eTL Trip Log
  // while createdAt and short trip-update id ensure uniqueness across multiple maintenance
  // entries on the same trip (Veryon rejects duplicate TechLog numbers).
  techLogNo: (input: TechLogToRusadaTripUpdateInput) => {
    const tripUpdate = input.techlog?.tripUpdate;
    return formatRusadaTechLogNo({
      tripNumber: tripUpdate?.number,
      entityId: tripUpdate?.id,
      instantPrefix: 'T',
      middleParts: [formatRusadaTechLogTimestamp(tripUpdate?.createdAt)],
    });
  },

  techLogDateUtc: (input: TechLogToRusadaTripUpdateInput) => {
    const tripUpdateDate = input.techlog?.tripUpdate?.date;
    if (tripUpdateDate) {
      return new Date(`${tripUpdateDate}T00:00:00Z`).toISOString();
    }
    return new Date().toISOString();
  },

  registrationId: (input: TechLogToRusadaTripUpdateInput) =>
    input.rusada?.registration?.id,

  // Use Maintenance TechLog Category for trip updates to avoid sector overlap conflicts
  // Look for isMaintenanceEntry: true, fallback to regular Tech Log category (1)
  techLogCategoryId: (input: TechLogToRusadaTripUpdateInput) => {
    const categories = input.rusada?.techLogCategories || [];
    const maintenanceCategory = categories.find(
      (cat) => cat.isMaintenanceEntry === true,
    );
    if (maintenanceCategory) {
      return maintenanceCategory.id;
    }
    // Fallback to regular Tech Log category (id: 1) if no maintenance category configured
    // This will cause overlap errors but is the best we can do without proper config
    return 1;
  },

  // Use "Not Flown" journey type for Affinity, fallback to EGR for other customers
  // Look for type: "Not Flown", fallback to EGR (id: 6)
  flightTypeId: (input: TechLogToRusadaTripUpdateInput) => {
    const flightTypes = input.rusada?.flightTypes || [];
    const notFlownType = flightTypes.find(
      (ft) => ft.type.toLowerCase() === 'not flown',
    );
    if (notFlownType) {
      return notFlownType.id;
    }
    // Fallback to Engine Ground Run (id: 6)
    return 6;
  },

  sectors: (input: TechLogToRusadaTripUpdateInput) => {
    const tripUpdate = input.techlog?.tripUpdate;
    const utilisation = tripUpdate?.utilisation;
    const defaultPlaceId = input.rusada?.defaultPlaceId;

    const flightMinutes = utilisation?.airframe_seconds
      ? Math.round(utilisation.airframe_seconds / 60)
      : 0;

    // Trip updates don't have actual departure/arrival times - they're utilisation adjustments.
    // Set both times equal; the flight hours are recorded via lifecodes, not time delta.
    const sectorTime = new Date(tripUpdate?.createdAt || Date.now());
    const takeoffTime = sectorTime;
    const landingTime = sectorTime;

    // Trip updates only have total landings count - no roll/FS breakdown
    const totalLandings = utilisation?.airframe_landings
      ? Math.round(utilisation.airframe_landings)
      : 0;
    const cycles = utilisation?.airframe_cycles
      ? Math.round(utilisation.airframe_cycles)
      : 0;
    // Engine cycles come from the per-engine builtin utilisation fields.
    // Convention: engine 1 → LH, engine 2 → RH. Single-engine aircraft use engine 1.
    const engine1Cycles = utilisation?.engine_1_cycles
      ? Math.round(utilisation.engine_1_cycles)
      : 0;
    const engine2Cycles = utilisation?.engine_2_cycles
      ? Math.round(utilisation.engine_2_cycles)
      : 0;
    // Engine starts come from dedicated trip-update custom fields (LH_ES_TU/RH_ES_TU).
    // TechLog reports a field that is configured on the aircraft but left empty as
    // null, and Veryon requires a numeric usage, so only a number counts as a value.
    const customData = tripUpdate?.customData;
    const lhEngineStarts =
      typeof customData?.LH_ES_TU === 'number'
        ? customData.LH_ES_TU
        : undefined;
    const rhEngineStarts =
      typeof customData?.RH_ES_TU === 'number'
        ? customData.RH_ES_TU
        : undefined;
    // POB: Trip updates don't have passenger data, use 0
    const pob = 0;

    const registrationLifeCodes = input.rusada?.registrationLifeCodes;
    const lifeCodes: Array<{
      locationId: number;
      locationPositionId: number;
      lifeCodeId: number;
      usage: number;
    }> = [];

    if (registrationLifeCodes?.length) {
      const findLifeCode = (
        lifeCodeName: string,
        locationName: string,
        positionName: string,
      ) => {
        return registrationLifeCodes.find(
          (lc) =>
            lc.lifeCode.toUpperCase() === lifeCodeName.toUpperCase() &&
            lc.location.toUpperCase() === locationName.toUpperCase() &&
            lc.locationPosition.toUpperCase() === positionName.toUpperCase(),
        );
      };

      // AIRFRAME lifecodes
      const airframeFlightHours = findLifeCode('FLIGHT HOURS', 'AIRFRAME', '');
      if (airframeFlightHours) {
        lifeCodes.push({
          locationId: airframeFlightHours.locationId,
          locationPositionId: airframeFlightHours.locationPositionId,
          lifeCodeId: airframeFlightHours.lifeCodeId,
          usage: flightMinutes,
        });
      }

      // AERO HOURS - placeholder: same as flight hours until custom field available in TechLog
      const airframeAeroHours = findLifeCode('AERO HOURS', 'AIRFRAME', '');
      if (airframeAeroHours) {
        lifeCodes.push({
          locationId: airframeAeroHours.locationId,
          locationPositionId: airframeAeroHours.locationPositionId,
          lifeCodeId: airframeAeroHours.lifeCodeId,
          usage: flightMinutes, // Placeholder: same as flight hours until TechLog custom field exists
        });
      }

      const airframeTotalLandings = findLifeCode(
        'TOTAL LANDINGS',
        'AIRFRAME',
        '',
      );
      if (airframeTotalLandings) {
        lifeCodes.push({
          locationId: airframeTotalLandings.locationId,
          locationPositionId: airframeTotalLandings.locationPositionId,
          lifeCodeId: airframeTotalLandings.lifeCodeId,
          usage: totalLandings,
        });
      }

      // Roll and FS landings not included - trip updates only have total landings count

      const airframePob = findLifeCode('POB', 'AIRFRAME', '');
      if (airframePob) {
        lifeCodes.push({
          locationId: airframePob.locationId,
          locationPositionId: airframePob.locationPositionId,
          lifeCodeId: airframePob.lifeCodeId,
          usage: pob,
        });
      }

      // Airframe-level Engine Starts take the RH (No2) engine-start count, which is
      // applied to both aircraft batteries (SS-1295). Emitted only when the RH
      // custom field is present, mirroring the per-engine start guard below.
      const airframeEngineStarts = findLifeCode(
        'Engine Starts',
        'AIRFRAME',
        '',
      );
      if (airframeEngineStarts && rhEngineStarts !== undefined) {
        lifeCodes.push({
          locationId: airframeEngineStarts.locationId,
          locationPositionId: airframeEngineStarts.locationPositionId,
          lifeCodeId: airframeEngineStarts.lifeCodeId,
          usage: rhEngineStarts,
        });
      }

      // 72-00 SINGLE ENGINE lifecodes (no position)
      // These appear on single-engine aircraft (T-6C Texan). Single-engine uses engine 1.
      const engineCyclesSingle = findLifeCode('ENGINE CYCLES', '72-00', '');
      if (engineCyclesSingle) {
        lifeCodes.push({
          locationId: engineCyclesSingle.locationId,
          locationPositionId: engineCyclesSingle.locationPositionId,
          lifeCodeId: engineCyclesSingle.lifeCodeId,
          usage: engine1Cycles,
        });
      }

      const engineHours = findLifeCode('ENGINE HOURS', '72-00', '');
      if (engineHours) {
        lifeCodes.push({
          locationId: engineHours.locationId,
          locationPositionId: engineHours.locationPositionId,
          lifeCodeId: engineHours.lifeCodeId,
          usage: flightMinutes, // Engine hours = flight hours
        });
      }

      // 72-00 TWIN ENGINE lifecodes (RH and LH positions)
      // These appear on twin-engine aircraft (Phenom EMB-500)
      // For twin engines, all values mirror the airframe values per business requirement
      for (const positionName of ['RH', 'LH']) {
        const twinFlightHours = findLifeCode(
          'FLIGHT HOURS',
          '72-00',
          positionName,
        );
        if (twinFlightHours) {
          lifeCodes.push({
            locationId: twinFlightHours.locationId,
            locationPositionId: twinFlightHours.locationPositionId,
            lifeCodeId: twinFlightHours.lifeCodeId,
            usage: flightMinutes,
          });
        }

        const twinTotalLandings = findLifeCode(
          'TOTAL LANDINGS',
          '72-00',
          positionName,
        );
        if (twinTotalLandings) {
          lifeCodes.push({
            locationId: twinTotalLandings.locationId,
            locationPositionId: twinTotalLandings.locationPositionId,
            lifeCodeId: twinTotalLandings.lifeCodeId,
            usage: totalLandings,
          });
        }

        const twinEngineStarts = findLifeCode(
          'Engine Starts',
          '72-00',
          positionName,
        );
        const positionEngineStarts =
          positionName === 'RH' ? rhEngineStarts : lhEngineStarts;
        if (twinEngineStarts && positionEngineStarts !== undefined) {
          lifeCodes.push({
            locationId: twinEngineStarts.locationId,
            locationPositionId: twinEngineStarts.locationPositionId,
            lifeCodeId: twinEngineStarts.lifeCodeId,
            usage: positionEngineStarts,
          });
        }

        const twinEngineCyclesPhenom = findLifeCode(
          'Engine Cycles Phenom',
          '72-00',
          positionName,
        );
        const positionEngineCycles =
          positionName === 'RH' ? engine2Cycles : engine1Cycles;
        if (twinEngineCyclesPhenom) {
          lifeCodes.push({
            locationId: twinEngineCyclesPhenom.locationId,
            locationPositionId: twinEngineCyclesPhenom.locationPositionId,
            lifeCodeId: twinEngineCyclesPhenom.lifeCodeId,
            usage: positionEngineCycles,
          });
        }
      }
    }

    return [
      {
        departurePlaceId: defaultPlaceId,
        arrivalPlaceId: defaultPlaceId,
        takeOffTimeUtc: takeoffTime.toISOString(),
        landingTimeUtc: landingTime.toISOString(),
        flightHours: flightMinutes,
        landings: totalLandings,
        cycles,
        lifeCodes: lifeCodes.length > 0 ? lifeCodes : undefined,
      },
    ];
  },
};

export default techlogTripUpdateToRusadaSchema;
